import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDropzone } from "react-dropzone";
import {
  FileText,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  CalendarDays,
  MessageSquare,
  Download,
  Upload,
} from "lucide-react";
import { getAccessToken } from "../../services/authApi";
import { fetchConditions } from "../../services/conditionsApi";
import { fetchAttachmentDetail } from "../../services/documentsApi";
import { fieldReader } from "../../services/loanApi";
import {
  fetchLatestApprovalCertificate as fetchApprovalCertificateFromApi,
  downloadApprovalCertificate as downloadApprovalCertificateFromApi,
} from "../../services/documentApi";
import { uploadDocument } from "../../services/brokerDocUploadApi";
import { uploadConditionDocument } from "../../services/brokerDocUploadApi";
import ConditionComments from "./ConditionComments";
import conditionDefinitions from "../menu/conditions.json";
import "./conditionUpdated.css";
import "../brokerdocupload/Documents.css";

const IsMock = false;
const isConditionBetaEnabled = process.env.REACT_APP_ENABLE_CONDITIONBETA_PAGE === "true";

const ACCEPTED_FILE_TYPES = {
  "application/pdf": [".pdf"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const formatDate = (dateStr) => {
  if (!dateStr) return "\u2014";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const getGroupTitle = (condition) => {
  const prior = condition.priorTo || "Other";
  const group = condition.conditionGroupName || "General";
  return `Prior to ${prior} - ${group}`;
};

const ConditionUpdated = () => {
  const [loanId, setLoanId] = useState("");
  const [canUploadDocs, setCanUploadDocs] = useState(false);
  const [canUploadConditionDocs, setCanUploadConditionDocs] = useState(false);
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("Needs Action");
  const [toast, setToast] = useState(null);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [openCommentsId, setOpenCommentsId] = useState(null);
  const [importantDates, setImportantDates] = useState({});
  const [datesExpanded, setDatesExpanded] = useState(true);
  const [priorToFilter, setPriorToFilter] = useState("All");
  const [conditionTypeFilter, setConditionTypeFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [approvalAttachmentIds, setApprovalAttachmentIds] = useState({
    conditional: null,
    final: null,
  });
  const rowFileInputsRef = useRef({});

  useEffect(() => {
    if (!IsMock && window.elli && window.elli.script && window.elli.script.guest) {
      try {
        window.elli.script.guest.create();
        window.elli.script
          .getObject("loan")
          .then((loanObject) =>
            loanObject.all().then((raw) => {
              const loan = raw?.loan ?? raw;
              const id = loan?.id;
              if (id) setLoanId(id);

              if (!isConditionBetaEnabled) {
                console.log("Condition Beta is disabled. Upload features will be hidden.");
                return;
              }

              const indexResponsibilityField = loan?.customFields?.find(
                (f) => f.fieldName === "CX.COND.INDEXRESPONSIBILITY"
              );
              const indexResponsibility = (indexResponsibilityField?.value || "")
                .toString()
                .trim()
                .toLowerCase();
              setCanUploadDocs(indexResponsibility !== "brokeronly");
              setCanUploadConditionDocs(indexResponsibility === "brokeronly");
            })
          );
      } catch (err) {
        console.error("Error initializing Encompass context:", err);
      }
    }
  }, []);

  const getAuthCode = async () => {
    const authObj = await window.elli.script.getObject("auth");
    return authObj.createAuthCode();
  };

  const getToken = useCallback(async () => {
    const authCode = await getAuthCode();
    const token = await getAccessToken(authCode);
    if (!token) throw new Error("Unable to acquire access token");
    return token;
  }, []);

  const fetchConditionsData = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const data = await fetchConditions(loanId, token);
      setConditions(data || []);
    } catch (error) {
      console.error("Failed to load conditions:", error);
      setConditions([]);
    } finally {
      setLoading(false);
    }
  }, [loanId, getToken]);

  useEffect(() => {
    if (loanId) {
      fetchConditionsData();
    }
  }, [loanId, fetchConditionsData]);

  useEffect(() => {
    const fetchImportantDates = async () => {
      if (!loanId || IsMock) return;
      try {
        const token = await getToken();
        const fieldIds = ["2301", "2302", "762", "2354", "ANALYZER.X20", "ANALYZER.X21", "ANALYZER.X19", "1034"];
        const values = await fieldReader(token, fieldIds, loanId);
        setImportantDates(values || {});
      } catch (err) {
        console.error("Failed to fetch important dates:", err);
      }
    };
    fetchImportantDates();
  }, [loanId, getToken]);

  useEffect(() => {
    const fetchApprovalCert = async () => {
      if (!loanId || IsMock) return;
      try {
        const authCode = await getAuthCode();
        const result = await fetchApprovalCertificateFromApi(authCode, loanId);
        if (result) {
          setApprovalAttachmentIds({
            conditional: result.conditionalAttachmentId || null,
            final: result.finalAttachmentId || null,
          });
        }
      } catch (err) {
        console.error("Failed to fetch approval certificate:", err);
      }
    };
    fetchApprovalCert();
  }, [loanId]);

  const hasApprovalLetter = !!(approvalAttachmentIds.conditional || approvalAttachmentIds.final);

  const downloadApprovalCertificate = async () => {
    try {
      const authCode = await getAuthCode();
      const attachmentId = approvalAttachmentIds.final || approvalAttachmentIds.conditional;
      const type = approvalAttachmentIds.final ? "final" : "conditional";
      const blob = await downloadApprovalCertificateFromApi(authCode, loanId, attachmentId, type);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = type === "final" ? "Final_Approval_Certificate.pdf" : "Conditional_Approval_Certificate.pdf";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download approval certificate failed:", err);
    }
  };

  const dashboard = useMemo(() => {
    const total = conditions.length;
    const needsAction = conditions.filter((c) => c.status === "Needs Action").length;
    const inReview = conditions.filter((c) => c.status === "In Review").length;
    const cleared = conditions.filter((c) => c.status === "Cleared").length;
    const docs = conditions.reduce((sum, c) => {
      const files = (c.folders || []).reduce((fileSum, folder) => fileSum + (folder.attachments || []).length, 0);
      return sum + files;
    }, 0);
    return { total, needsAction, inReview, cleared, docs };
  }, [conditions]);

  const priorToOptions = useMemo(() => {
    const values = [...new Set(conditions.map((c) => c.priorTo).filter(Boolean))];
    return ["All", ...values];
  }, [conditions]);

  const conditionTypeOptions = useMemo(() => {
    const values = [...new Set(conditions.map((c) => c.conditionType).filter(Boolean))];
    return ["All", ...values];
  }, [conditions]);

  const categoryOptions = useMemo(() => {
    const groups = [...new Set(conditions.map((c) => c.conditionGroupName).filter(Boolean))];
    return ["All", ...groups];
  }, [conditions]);

  const filteredConditions = useMemo(() => {
    let result = [...conditions];

    if (activeTab === "Needs Action") {
      result = result.filter((c) => c.status === "Needs Action");
    }
    if (activeTab === "In Review") {
      result = result.filter((c) => c.status === "In Review");
    }
    if (activeTab === "Cleared") {
      result = result.filter((c) => c.status === "Cleared");
    }

    if (priorToFilter !== "All") {
      result = result.filter((c) => c.priorTo === priorToFilter);
    }

    if (conditionTypeFilter !== "All") {
      result = result.filter((c) => c.conditionType === conditionTypeFilter);
    }

    if (categoryFilter !== "All") {
      result = result.filter((c) => c.conditionGroupName === categoryFilter);
    }

    if (appliedSearch.trim()) {
      const term = appliedSearch.trim().toLowerCase();
      result = result.filter((c) => {
        if ((c.externalId || "").toLowerCase().includes(term)) return true;
        if ((c.conditionName || "").toLowerCase().includes(term)) return true;
        if ((c.conditionDescription || "").toLowerCase().includes(term)) return true;
        const fileNames = (c.folders || []).flatMap((folder) =>
          (folder.attachments || []).map((att) => (att.fileName || "").toLowerCase())
        );
        return fileNames.some((name) => name.includes(term));
      });
    }

    result.sort((a, b) => getGroupTitle(a).localeCompare(getGroupTitle(b)));

    return result;
  }, [conditions, activeTab, priorToFilter, conditionTypeFilter, categoryFilter, appliedSearch]);

  // Pagination disabled: show all filtered conditions
  // const totalPages = Math.ceil(filteredConditions.length / PAGE_SIZE);
  // const paginatedConditions = useMemo(() => {
  //   const start = (currentPage - 1) * PAGE_SIZE;
  //   return filteredConditions.slice(start, start + PAGE_SIZE);
  // }, [filteredConditions, currentPage]);

  const groupedConditions = useMemo(() => {
    const map = new Map();
    filteredConditions.forEach((condition) => {
      const key = getGroupTitle(condition);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(condition);
    });
    return Array.from(map.entries()).map(([groupTitle, items]) => ({ groupTitle, items }));
  }, [filteredConditions]);

  const handleFileDownload = async (attachmentId, fileName) => {
    try {
      const token = await getToken();
      const detail = await fetchAttachmentDetail(loanId, attachmentId, token);
      if (!detail?.url) return;
      const response = await fetch(detail.url, {
        headers: detail.authorizationHeader ? { Authorization: detail.authorizationHeader } : {},
      });
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName || "document";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const handleConditionUpload = useCallback(async (files, condition) => {
    if (!files?.length || !loanId) return;

    const conditionId =
      condition?.conditionId || condition?.internalId || condition?.conditionId;
    const conditionExternalId =
      condition?.externalId || condition?.conditionExternalId || condition?.conditionId;

    if (!conditionId || !conditionExternalId) {
      setToast({
        message: "Unable to upload: missing condition identifiers.",
        type: "error",
      });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    const newQueue = files.map((file) => ({
      id: `upload-${conditionId}-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      conditionId: conditionId,
      conditionExternalId: conditionExternalId,
      file,
      progress: 0,
      status: "uploading",
    }));

    setUploadQueue((prev) => [...prev, ...newQueue]);

    let token;
    try {
      token = await getToken();
    } catch {
      setUploadQueue((prev) =>
        prev.map((q) =>
          newQueue.find((n) => n.id === q.id)
            ? { ...q, status: "error", progress: 0 }
            : q
        )
      );
      setToast({ message: "Unable to authenticate upload.", type: "error" });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    for (const item of newQueue) {
      try {
        await uploadConditionDocument(
          item.file,
          token,
          loanId,
          conditionExternalId,
          conditionId,
          (progress) => {
            setUploadQueue((prev) => prev.map((q) => (q.id === item.id ? { ...q, progress } : q)));
          }
        );

        setUploadQueue((prev) =>
          prev.map((q) => (q.id === item.id ? { ...q, status: "done", progress: 100 } : q))
        );

        setToast({
          message: `${item.file.name} uploaded for condition ${condition.externalId || condition.conditionId}.`,
          type: "success",
        });
        setTimeout(() => setToast(null), 3000);
      } catch {
        setUploadQueue((prev) =>
          prev.map((q) => (q.id === item.id ? { ...q, status: "error", progress: 0 } : q))
        );
        setToast({
          message: `Failed to upload ${item.file.name} for condition ${condition.externalId || condition.conditionId}.`,
          type: "error",
        });
        setTimeout(() => setToast(null), 3000);
      }
    }

    setTimeout(() => {
      setUploadQueue((prev) => prev.filter((q) => q.conditionId !== condition.conditionId));
    }, 1800);

    fetchConditionsData();
  }, [loanId, getToken, fetchConditionsData]);

  const handleConditionFileChange = (event, condition) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const acceptedMimeTypes = Object.keys(ACCEPTED_FILE_TYPES);
    const invalidFiles = files.filter(
      (file) => !acceptedMimeTypes.includes(file.type) || file.size > MAX_FILE_SIZE
    );

    if (invalidFiles.length > 0) {
      setToast({
        message: `Some files are invalid. Allowed: PDF/JPG/PNG, max 5MB.`,
        type: "error",
      });
      setTimeout(() => setToast(null), 3000);
    }

    const validFiles = files.filter(
      (file) => acceptedMimeTypes.includes(file.type) && file.size <= MAX_FILE_SIZE
    );

    if (validFiles.length > 0) {
      handleConditionUpload(validFiles, condition);
    }

    event.target.value = "";
  };

  const handleLoanDocumentUpload = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles?.length || !loanId) return;

    const newQueue = acceptedFiles.map((file) => ({
      id: `upload-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      file,
      progress: 0,
      status: "uploading",
    }));

    setUploadQueue((prev) => [...prev, ...newQueue]);

    let token;
    try {
      token = await getToken();
    } catch {
      setUploadQueue((prev) =>
        prev.map((q) =>
          newQueue.find((n) => n.id === q.id)
            ? { ...q, status: "error", progress: 0 }
            : q
        )
      );
      setToast({ message: "Unable to authenticate upload.", type: "error" });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    for (const item of newQueue) {
      try {
        await uploadDocument(item.file, token, loanId, "", (progress) => {
          setUploadQueue((prev) => prev.map((q) => (q.id === item.id ? { ...q, progress } : q)));
        });

        setUploadQueue((prev) =>
          prev.map((q) => (q.id === item.id ? { ...q, status: "done", progress: 100 } : q))
        );

        setToast({ message: `${item.file.name} uploaded successfully.`, type: "success" });
        setTimeout(() => setToast(null), 3000);
      } catch {
        setUploadQueue((prev) =>
          prev.map((q) => (q.id === item.id ? { ...q, status: "error", progress: 0 } : q))
        );
        setToast({ message: `Failed to upload ${item.file.name}.`, type: "error" });
        setTimeout(() => setToast(null), 3000);
      }
    }

    setTimeout(() => {
      setUploadQueue((prev) => prev.filter((q) => !newQueue.find((n) => n.id === q.id)));
    }, 1500);

    fetchConditionsData();
  }, [loanId, getToken, fetchConditionsData]);

  const onLoanDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      const messages = rejectedFiles.map((r) => {
        const errors = r.errors.map((e) => e.message).join(", ");
        return `${r.file.name}: ${errors}`;
      });
      setToast({ message: `Some files were rejected: ${messages.join(" | ")}`, type: "error" });
      setTimeout(() => setToast(null), 4000);
    }

    if (acceptedFiles.length > 0) {
      handleLoanDocumentUpload(acceptedFiles);
    }
  }, [handleLoanDocumentUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onLoanDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
  });

  const loanUploadQueue = uploadQueue.filter((item) => !item.conditionId);

  const requiredConditionKeys = useMemo(() => {
    const requiredDefs = (Array.isArray(conditionDefinitions) ? conditionDefinitions : [])
      .filter((conditionDef) => conditionDef?.required === true);

    const internalIdSet = new Set(
      requiredDefs
        .map((conditionDef) => (conditionDef?.internalId || "").toString().trim().toUpperCase())
        .filter(Boolean)
    );

    const externalIdSet = new Set(
      requiredDefs
        .map((conditionDef) => (conditionDef?.externalId || "").toString().trim().toUpperCase())
        .filter(Boolean)
    );

    return { internalIdSet, externalIdSet };
  }, []);

  const isRequiredCondition = useCallback((condition) => {
    const conditionInternalId = (condition?.internalId || "")
      .toString()
      .trim()
      .toUpperCase();
    const conditionExternalId = (condition?.externalId || condition?.conditionExternalId || "")
      .toString()
      .trim()
      .toUpperCase();

    return requiredConditionKeys.internalIdSet.has(conditionInternalId)
      || requiredConditionKeys.externalIdSet.has(conditionExternalId);
  }, [requiredConditionKeys]);

  return (
    <div className="cupd-container">
      <div className="cupd-header">
        <div className="cupd-header-top">
          <div>
            <h1>Conditions</h1>
            <p>{hasApprovalLetter
              ? "This mortgage loan has been approved on behalf of the above noted client with the following terms and conditions."
              : "Review and manage all conditions for your loan."}</p>
          </div>
          <div className="cupd-header-actions">
            {hasApprovalLetter && (
              <button className="cupd-approval-btn" onClick={downloadApprovalCertificate}>
                <Download size={15} />
                Last Printed Approval
              </button>
            )}
            <button className="cupd-refresh" onClick={fetchConditionsData} disabled={loading}>
              <RefreshCw size={15} className={loading ? "cupd-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {canUploadDocs && (
          <section
            {...getRootProps()}
            className={`docs-upload cupd-loan-upload ${isDragActive ? "docs-upload-active" : ""}`}
          >
            <input {...getInputProps()} />
            <div className="docs-upload-left">
              <Upload size={20} />
              <div>
                <h3>Upload documents</h3>
                <p>Drag and drop files here or click to browse.</p>
              </div>
            </div>
            <button type="button">Browse Files</button>
          </section>
        )}
      </div>

      {loanUploadQueue.length > 0 && (
        <section className="docs-upload-list">
          {loanUploadQueue.map((item) => (
            <div key={item.id} className="docs-upload-row">
              <div className="docs-upload-row-main">
                <div className="docs-upload-row-head">
                  <span className="docs-upload-file-name">
                    <FileText size={14} />
                    {item.file.name}
                  </span>
                  <span>{item.status === "error" ? "Failed" : `${item.progress}%`}</span>
                </div>
                <div className="docs-upload-progress-track">
                  <div
                    className={`docs-upload-progress-fill ${item.status === "error" ? "docs-upload-progress-error" : ""}`}
                    style={{ width: `${item.progress}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </section>
      )}

      <section className="cupd-dates">
        <button className="cupd-dates-head" onClick={() => setDatesExpanded((p) => !p)}>
          <span className="cupd-dates-title">
            <CalendarDays size={16} />
            Approval Information
          </span>
          {datesExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {datesExpanded && (
          <div className="cupd-dates-grid">
            <div className="cupd-date-item"><span>Approval Date:</span><strong>{!importantDates["2301"] || importantDates["2301"] === "//" ? "\u2014" : importantDates["2301"]}</strong></div>
            <div className="cupd-date-item"><span>Approval Exp Date:</span><strong>{!importantDates["2302"] || importantDates["2302"] === "//" ? "\u2014" : importantDates["2302"]}</strong></div>
            <div className="cupd-date-item"><span>Appraisal Exp Date:</span><strong>{!importantDates["2354"] || importantDates["2354"] === "//" ? "\u2014" : importantDates["2354"]}</strong></div>
            <div className="cupd-date-item"><span>Credit Exp Date:</span><strong>{!importantDates["ANALYZER.X19"] || importantDates["ANALYZER.X19"] === "//" ? "\u2014" : importantDates["ANALYZER.X19"]}</strong></div>
            <div className="cupd-date-item"><span>Rate Lock Exp:</span><strong>{!importantDates["762"] || importantDates["762"] === "//" ? "\u2014" : importantDates["762"]}</strong></div>
            <div className="cupd-date-item"><span>Title Exp Date:</span><strong>{!importantDates["1034"] || importantDates["1034"] === "//" ? "\u2014" : importantDates["1034"]}</strong></div>
            <div className="cupd-date-item"><span>Income Exp Date:</span><strong>{!importantDates["ANALYZER.X20"] || importantDates["ANALYZER.X20"] === "//" ? "\u2014" : importantDates["ANALYZER.X20"]}</strong></div>
            <div className="cupd-date-item"><span>Asset Exp Date:</span><strong>{!importantDates["ANALYZER.X21"] || importantDates["ANALYZER.X21"] === "//" ? "\u2014" : importantDates["ANALYZER.X21"]}</strong></div>
          </div>
        )}
      </section>



      <section className="cupd-table-wrap">
        <div className="cupd-filter-row">
          <div className="cupd-status-tabs">
            <button className={activeTab === "All" ? "active" : ""} onClick={() => setActiveTab("All")}>All ({dashboard.total})</button>
            <button className={activeTab === "Needs Action" ? "active" : ""} onClick={() => setActiveTab("Needs Action")}>Action required from you ({dashboard.needsAction})</button>
            <button className={activeTab === "In Review" ? "active" : ""} onClick={() => setActiveTab("In Review")}>Underwriter is reviewing ({dashboard.inReview})</button>
            <button className={activeTab === "Cleared" ? "active" : ""} onClick={() => setActiveTab("Cleared")}>Cleared ({dashboard.cleared})</button>
          </div>
          <div className="cupd-controls-wrap">
            <div className="cupd-search-wrap">
              <input
                type="text"
                className="cupd-search-input"
                placeholder="Search conditions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setAppliedSearch(searchTerm);
                  }
                }}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="cupd-search-clear"
                  onClick={() => {
                    setSearchTerm("");
                    setAppliedSearch("");
                  }}
                >
                  &times;
                </button>
              )}
            </div>
            <select
              className="cupd-filter-select"
              value={priorToFilter}
              onChange={(e) => setPriorToFilter(e.target.value)}
            >
              {priorToOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt === "All" ? "Filter By: Prior To" : opt}
                </option>
              ))}
            </select>
            <select
              className="cupd-filter-select"
              value={conditionTypeFilter}
              onChange={(e) => setConditionTypeFilter(e.target.value)}
            >
              {conditionTypeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt === "All" ? "Filter By: Condition Type" : opt}
                </option>
              ))}
            </select>
            <select
              className="cupd-filter-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              {categoryOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt === "All" ? "Filter By: Category" : opt}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="cupd-table-head">
          <span>Id</span>
          <span>Condition</span>
          <span>Files</span>
          {canUploadConditionDocs && <span>Upload</span>}
          <span>Comments</span>
        </div>

        {loading && <div className="cupd-loading">Loading conditions...</div>}

        {!loading && groupedConditions.length === 0 && (
          <div className="cupd-empty">No conditions available for this filter.</div>
        )}

        {!loading && groupedConditions.map((group) => (
          <div key={group.groupTitle} className="cupd-group">
            <div className="cupd-group-head">{group.groupTitle} ({group.items.length})</div>
            {group.items.map((condition) => (
              <React.Fragment key={condition.conditionId}>
                <div className={`cupd-row ${isRequiredCondition(condition) ? "cupd-row-required" : ""}`}>
                  <div className="cupd-id">{condition.externalId || "\u2014"}</div>
                  <div className="cupd-condition">
                    <p>{condition.conditionDescription || "\u2014"}</p>
                    {isRequiredCondition(condition) && <span className="cupd-required-chip">Required</span>}
                  </div>
                  <div className="cupd-files">
                    {(() => {
                      const attachments = (condition.folders || []).flatMap((folder) =>
                        (folder.attachments || []).filter(Boolean)
                      );

                      if (attachments.length === 0) {
                        return <span className="cupd-no-files">-</span>;
                      }

                      return attachments.map((att) => (
                        <button
                          type="button"
                          key={att.attachmentId}
                          className="cupd-file-link"
                          onClick={() => handleFileDownload(att.attachmentId, att.fileName)}
                        >
                          <FileText size={14} />
                          {att.fileName || "document"}
                        </button>
                      ));
                    })()}
                  </div>
                  {canUploadConditionDocs && (
                    <div className="cupd-upload-col">
                      <>
                        <input
                          ref={(el) => {
                            rowFileInputsRef.current[condition.conditionId] = el;
                          }}
                          type="file"
                          hidden
                          aria-hidden="true"
                          tabIndex={-1}
                          className="cupd-row-upload-input"
                          accept=".pdf,.jpg,.jpeg,.png"
                          multiple
                          onChange={(event) => handleConditionFileChange(event, condition)}
                        />
                        <button
                          type="button"
                          className="cupd-upload-btn"
                          title="Upload files"
                          onClick={() => rowFileInputsRef.current[condition.conditionId]?.click()}
                        >
                          <Upload size={14} />
                        </button>
                        {uploadQueue.some((q) => q.conditionId === condition.conditionId && q.status === "uploading") && (
                          <span className="cupd-uploading-tag">Uploading...</span>
                        )}
                      </>

                    </div>
                  )}
                  <div className="cupd-comments">
                    <button
                      type="button"
                      className={`cupd-comment-btn ${(condition.commentsCount || 0) > 0 ? "has-count" : ""}`}
                      onClick={() => setOpenCommentsId(openCommentsId === condition.conditionId ? null : condition.conditionId)}
                    >
                      <span className="cupd-comment-icon-wrap">
                        <MessageSquare size={20} />
                        {(condition.commentsCount || 0) > 0 && (
                          <span className="cupd-comment-badge">{condition.commentsCount}</span>
                        )}
                      </span>
                    </button>
                  </div>
                </div>
                {openCommentsId === condition.conditionId && (
                  <ConditionComments
                    loanId={loanId}
                    conditionId={condition.conditionId}
                    getToken={getToken}
                    onClose={() => setOpenCommentsId(null)}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        ))}

      </section>

      {toast && <div className={`cupd-toast cupd-toast-${toast.type}`}>{toast.message}</div>}
    </div>
  );
};

export default ConditionUpdated;
